import React, { useState, useEffect, useRef } from 'react';
import LocationList from '../LocationList';
import { useMto } from '../../context/MtoContext';
import '../styles/modes/autoMode.css';

import ModeHelpTooltip from './ModeHelpTooltip';
import AlgorithmDetails from './AlgorithmDetails';
import SolveResult from './SolveResult';
import SolverActions from './SolverActions';
import ErrorDisplay from './ErrorDisplay';
import { useAlgorithms } from './hooks/useAlgorithms';
import { useSolveMto } from './hooks/useSolveMto';
import { getAlgorithmOptions } from './utils';
import { isAutoMode, calculatePathDistance } from '../../Services';

export default function AutoMode({ 
  locations, 
  startId, 
  endId,
  setLocations, 
  setStartId, 
  setEndId,
  setPath, 
  path,
  currentModeRef,
  demand,
  modeCapacities
}) {
  // Track previous values to detect changes
  const prevLocationsRef = useRef(null);
  const prevStartIdRef = useRef(null);
  const prevEndIdRef = useRef(null);
  
  // Help tooltip visibility
  const [showModeHelp, setShowModeHelp] = useState(false);
  
  // Get connection data and persistent solve result from context
  const { 
    connections, 
    autoSolveResult: solveResult, 
    setAutoSolveResult: setSolveResult 
  } = useMto();
  
  // Initialize hooks for algorithms and solving
  const { 
    algorithms, 
    isLoadingAlgorithms, 
    selectedAlgorithm, 
    handleAlgorithmChange 
  } = useAlgorithms(demand);
  
  const { 
    isSolving, 
    solveError, 
    solvePath 
  } = useSolveMto(setPath, setSolveResult);

  // Reset solve result when start or end point changes
  useEffect(() => {
    // Only clear if start point actually changed to a different valid value
    if (prevStartIdRef.current !== null && startId && prevStartIdRef.current !== startId) {
      setSolveResult(null);
    }
    
    // Only clear if end point changed from one valid value to another valid value
    if (prevEndIdRef.current !== null && endId && prevEndIdRef.current !== endId) {
      setSolveResult(null);
    }
    
    prevStartIdRef.current = startId;
    prevEndIdRef.current = endId;
  }, [startId, endId, setSolveResult]);
  
  
  // Get algorithm info with fallback
  const algorithmInfo = algorithms[selectedAlgorithm] || 
                        algorithms['connection_shortest_path'] || 
                        algorithms['capacity_constrained_shortest_path'] ||
                        Object.values(algorithms)[0] ||
                        { name: 'Loading...', description: 'Please wait...', principle: '' };
  
  const toggleModeHelp = () => setShowModeHelp(!showModeHelp);
  
  useEffect(() => {
    // Only clear path if in Auto Mode AND something actually changed
    if (prevLocationsRef.current !== null && prevStartIdRef.current !== null) {
      const locationsChanged = 
        prevLocationsRef.current.length !== locations.length ||
        JSON.stringify(prevLocationsRef.current) !== JSON.stringify(locations);
      
      const startIdChanged = prevStartIdRef.current !== startId;
      
      // Only clear if we're currently in Auto Mode
      if ((locationsChanged || startIdChanged) && currentModeRef && isAutoMode(currentModeRef.current)) {
        setPath([]);
        setSolveResult(null);
      }
    }
    
    // Store current values for next comparison
    prevLocationsRef.current = [...locations];
    prevStartIdRef.current = startId;
  }, [locations, startId, setPath, currentModeRef, setSolveResult]);
  
  // Calculate total travel time using connection data
  const calculatePathTotalTime = (path, connections) => {
    if (!path || path.length < 2 || !connections || connections.length === 0) {
      return 0;
    }

    let totalTime = 0;
    
    // Create lookup map for connection durations
    const connectionMap = new Map();
    connections.forEach(conn => {
      const key1 = `${conn.points[0]?.id}-${conn.points[1]?.id}`;
      const key2 = `${conn.points[1]?.id}-${conn.points[0]?.id}`;
      connectionMap.set(key1, conn.duration || 0);
      connectionMap.set(key2, conn.duration || 0);
    });

    // Sum up durations for each path segment
    for (let i = 0; i < path.length - 1; i++) {
      const fromId = path[i];
      const toId = path[i + 1];
      const connectionKey = `${fromId}-${toId}`;
      
      const duration = connectionMap.get(connectionKey);
      if (duration !== undefined) {
        totalTime += duration;
      }
    }

    return totalTime;
  };

  const handleSolve = () => {
    solvePath(locations, startId, endId, connections, selectedAlgorithm, demand, modeCapacities);
  };
  
  return (
    <div className="auto-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Auto Find Path</h3>
          <button 
            className="mode-help-button"
            onClick={toggleModeHelp}
            title="Help for Auto Path mode"
          >
            ?
          </button>
        </div>
        <p>Use algorithms to automatically find optimal or near-optimal paths.</p>
      </div>
      
      {/* Mode help tooltip */}
      {showModeHelp && <ModeHelpTooltip onClose={() => setShowModeHelp(false)} />}
      
          {/* Location list - can edit types and start point */}
      <LocationList 
        locations={locations}
        startId={startId}
        endId={endId}
        mode="Auto Find Path"
        readOnly={false}
        showActions={true}
        allowDelete={false}
        allowSetStart={true}
        allowSetEnd={true}
        onLocationChange={setLocations}
        onStartIdChange={setStartId}
        onEndIdChange={setEndId}
        showMaxCapacity={false}
      />
      
      {locations.length > 0 ? (
        <div className="auto-path-solver">
          {/* Show solve results */}
          <SolveResult 
            solveResult={solveResult} 
            algorithms={algorithms} 
            locations={locations} 
          />
          
          {/* Solver controls */}
          <SolverActions 
            locations={locations}
            startId={startId}
            isSolving={isSolving}
            isLoadingAlgorithms={isLoadingAlgorithms}
            selectedAlgorithm={selectedAlgorithm}
            handleAlgorithmChange={handleAlgorithmChange}
            solvePath={handleSolve}
            getAlgorithmOptions={() => getAlgorithmOptions(algorithms)}
          />
          
          {/* Error display */}
          <ErrorDisplay solveError={solveError} />
          
          {/* Algorithm information */}

          <AlgorithmDetails 
            algorithmInfo={algorithmInfo} 
            isLoadingAlgorithms={isLoadingAlgorithms} 
          />
        </div>
      ) : null}
    </div>
  );
} 